import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "./supabaseClient";
import { useAuth } from "./AuthContext";

const DEFAULT_SETTINGS = {
  sound:          false, // 소리 (브라우저 정책상 기본 off)
  popup:          true,  // 인앱 토스트 팝업
  match_request:  true,  // 매칭 신청 알림
  match_accepted: true,  // 수락 알림
  match_rejected: true,  // 거절 알림
  chat_message:   true,  // 채팅 메시지 알림
};

const NotificationContext = createContext(null);

export function NotificationProvider({ children }) {
  const { activeTeam, user } = useAuth();

  const [notifications, setNotifications] = useState([]);
  const [toast, setToast] = useState(null);
  const toastTimer = useRef(null);

  const [settings, setSettingsState] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem("mu_notif_settings"));
      return saved ? { ...DEFAULT_SETTINGS, ...saved } : DEFAULT_SETTINGS;
    } catch { return DEFAULT_SETTINGS; }
  });
  // 구독 콜백 안에서 최신 settings를 참조하기 위해 ref 사용
  const settingsRef = useRef(settings);
  useEffect(() => { settingsRef.current = settings; }, [settings]);

  // 로그인 시 DB에서 notif_settings 초기 로드
  useEffect(() => {
    if (!user?.id) return;
    supabase
      .from("device_tokens")
      .select("notif_settings")
      .eq("user_id", user.id)
      .order("updated_at", { ascending: false })
      .limit(1)
      .maybeSingle()
      .then(({ data }) => {
        if (data?.notif_settings) {
          const merged = { ...DEFAULT_SETTINGS, ...data.notif_settings };
          setSettingsState(merged);
          settingsRef.current = merged;
          localStorage.setItem("mu_notif_settings", JSON.stringify(merged));
        }
      })
      .catch(() => {}); // 토큰 없으면 localStorage 폴백 유지
  }, [user?.id]); // eslint-disable-line

  // 알림 목록 조회
  const fetchNotifications = useCallback(async () => {
    if (!activeTeam?.id) { setNotifications([]); return; }
    const { data } = await supabase
      .from("notifications")
      .select("*")
      .eq("team_id", activeTeam.id)
      .order("created_at", { ascending: false })
      .limit(50);
    setNotifications(data || []);
  }, [activeTeam]);

  useEffect(() => { fetchNotifications(); }, [fetchNotifications]);

  // Realtime 구독 — activeTeam이 바뀔 때만 재구독
  useEffect(() => {
    if (!activeTeam?.id) return;
    const channel = supabase
      .channel(`notif-${activeTeam.id}`)
      .on("postgres_changes", {
        event: "INSERT", schema: "public", table: "notifications",
        filter: `team_id=eq.${activeTeam.id}`,
      }, (payload) => {
        const notif = payload.new;
        setNotifications((prev) => [notif, ...prev]);
        const s = settingsRef.current;
        const typeOn = s[notif.type] ?? true;
        if (s.popup && typeOn) showToast(notif);
        if (s.sound && typeOn) playSound();
      })
      .subscribe();
    return () => supabase.removeChannel(channel);
  }, [activeTeam]);

  const showToast = (notif) => {
    setToast(notif);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 4000);
  };

  const playSound = () => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const osc  = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = 880;
      gain.gain.setValueAtTime(0.3, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.3);
      osc.start();
      osc.stop(ctx.currentTime + 0.3);
    } catch (_) {}
  };

  const markAsRead = async (id) => {
    await supabase.from("notifications").update({ is_read: true }).eq("id", id);
    setNotifications((prev) => prev.map((n) => n.id === id ? { ...n, is_read: true } : n));
  };

  const markAllAsRead = async () => {
    if (!activeTeam?.id) return;
    await supabase.from("notifications")
      .update({ is_read: true })
      .eq("team_id", activeTeam.id)
      .eq("is_read", false);
    setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
  };

  const updateSettings = (key, value) => {
    const next = { ...settings, [key]: value };
    setSettingsState(next);
    localStorage.setItem("mu_notif_settings", JSON.stringify(next));
    if (user?.id) {
      supabase
        .from("device_tokens")
        .update({ notif_settings: next, updated_at: new Date().toISOString() })
        .eq("user_id", user.id)
        .then(() => {})
        .catch(() => {}); // 토큰 row 없으면(웹 등) 조용히 무시
    }
  };

  const unreadCount = notifications.filter((n) => !n.is_read).length;

  return (
    <NotificationContext.Provider value={{
      notifications, unreadCount, toast, setToast,
      markAsRead, markAllAsRead,
      settings, updateSettings,
      fetchNotifications,
    }}>
      {children}
    </NotificationContext.Provider>
  );
}

export const useNotifications = () => useContext(NotificationContext);
